import { QRCodeCanvas } from 'qrcode.react';
import { Outlet, useNavigate, useOutletContext, useParams } from 'react-router';
import {
    RequestStatus,
    ServerRequestState,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from './ConfirmationDialog.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../../ui/modal/OperatorRoutedModal.tsx';
import { useState } from 'react';
import { Box, Button, Stack, TextField } from '@mui/material';
import * as OperatorLoginsState from '../state/OperatorLoginsState.ts';
import * as CommonOperatorLoginsState from '../../common/state/OperatorLoginsState.ts';
import { useUpdate } from 'dg-web-shared/lib/Flux.tsx';
import Alert from '@mui/material/Alert';
import { SubmitButton } from '../../ui/buttons/SubmitButton.tsx';
import * as CurrentOperatorLoginState from '../../common/state/CurrentOperatorLoginState.ts';

interface ConfirmOtpRequest {
    token: string;
}

export function ConfirmOtpModal() {
    const urlParams = useParams();
    const operatorLoginId = Number(urlParams.operatorLoginId);
    const otpUrl = urlParams.otpUrl;
    const [confirmOtpState, confirmOtp] = useServerWrite<
        ConfirmOtpRequest,
        null
    >(() => ({
        url: `/ui-api/operator-account/operator-logins/${operatorLoginId}/otp-confirm`,
    }));
    const navigate = useNavigate();
    const [otpToken, setOtpToken] = useState('');
    const update = useUpdate();
    const { refetchAfterSuccess } = useOtpOutletContext();
    useServerSuccessEffect(confirmOtpState, () => {
        update(store => {
            OperatorLoginsState.Edit.reset(store);
            CommonOperatorLoginsState.List.reset(store);
            CurrentOperatorLoginState.reset(store);
            return 'refetch-logins-after-otp-enable';
        });
        if (refetchAfterSuccess) {
            refetchAfterSuccess();
        }
        navigate('..');
    });
    if (isNaN(operatorLoginId) || !otpUrl) {
        return (
            <ConfirmationDialog
                open
                title={
                    <Localized de="Fehler" fr="Erreur" en="Error" it="Errore" />
                }
                confirmLabel={<Localized de="OK" fr="OK" it="OK" en="OK" />}
                onConfirm={() => navigate('..')}
                dialogVersion={ConfirmationDialogVersions.ERROR}
            >
                <p>
                    <Localized
                        de="Ein unerwarteter Fehler ist aufgetreten."
                        fr="Une erreur inattendue s'est produite."
                        it="Si è verificato un errore imprevisto."
                        en="An unexpected error has occurred."
                    />
                </p>
            </ConfirmationDialog>
        );
    }
    return (
        <OperatorRoutedModal
            variant={ModalVariant.SMALL}
            backUrl={'..'}
            style={{ height: undefined }}
            render={() => (
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflowY: 'auto',
                    }}
                >
                    <Box
                        sx={theme => ({
                            padding: theme.spacing(1.875, 3),
                            color: theme.palette.common.white,
                            backgroundColor: theme.palette.blue.main,
                            fontSize: '26px',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                        })}
                    >
                        <Localized
                            de="Zwei-Faktor-Authentifizierung"
                            fr="Authentification à deux facteurs"
                            it="Autenticazione a due fattori"
                            en="Two-Factor Authentication"
                        />
                    </Box>
                    <Box
                        sx={theme => ({
                            display: 'flex',
                            padding: '20px 24px',
                            justifyContent: 'space-between',
                            flexDirection: 'column',
                            flexGrow: 1,
                            color: theme.palette.blue.main,
                        })}
                    >
                        <Stack spacing={3}>
                            <ConfirmOtpErrorMessage
                                confirmOtpState={confirmOtpState}
                            />
                            <Box>
                                <Localized
                                    de="Bitte scannen Sie den untenstehenden QR-Code mit einer Authentifizierungs-App und geben Sie den angezeigten Code ein."
                                    fr="Veuillez scanner le code QR ci-dessous avec une application d'authentification et puis saisissez le code affiché."
                                    it="Scansionare il codice QR qui sotto con un'app di autenticazione e inserire il codice visualizzato."
                                    en="Please scan the QR code below using an authenticator app and enter the resulting code."
                                />
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                            >
                                <QRCodeCanvas
                                    value={decodeURIComponent(otpUrl)}
                                    size={256}
                                />
                            </Box>
                            <TextField
                                autoComplete="one-time-code"
                                label={
                                    <Localized
                                        de="Einmalcode"
                                        fr="Code à usage unique"
                                        it="Codice monouso"
                                        en="One Time Code"
                                    />
                                }
                                value={otpToken}
                                onChange={v => {
                                    const value = v.target.value;
                                    if (/^\d{0,6}$/.test(value)) {
                                        setOtpToken(value);
                                    }
                                }}
                                slotProps={{
                                    htmlInput: {
                                        inputMode: 'numeric',
                                        maxLength: 6,
                                    },
                                }}
                            />
                        </Stack>
                        <Stack
                            direction={'row-reverse'}
                            spacing={2}
                            sx={theme => ({ paddingTop: theme.spacing(3) })}
                        >
                            <SubmitButton
                                dirtyForm
                                onClick={() => confirmOtp({ token: otpToken })}
                                label={
                                    <Localized
                                        de="Speichern"
                                        fr="Sauvegarder"
                                        it="Salva"
                                        en="Save"
                                    />
                                }
                                disabled={otpToken.length !== 6}
                                loading={
                                    confirmOtpState.status ==
                                    RequestStatus.PENDING
                                }
                            />
                            <Button
                                onClick={() => navigate('..')}
                                variant="outlined"
                                disabled={
                                    confirmOtpState.status ==
                                    RequestStatus.PENDING
                                }
                            >
                                <Localized
                                    de="Abbrechen"
                                    fr="Annuler"
                                    it="Annulla"
                                    en="Cancel"
                                />
                            </Button>
                        </Stack>
                    </Box>
                </Box>
            )}
        />
    );
}

function ConfirmOtpErrorMessage({
    confirmOtpState,
}: {
    confirmOtpState: ServerRequestState<null, object>;
}) {
    if (confirmOtpState.status != RequestStatus.ERROR) {
        return null;
    }

    if (confirmOtpState.httpStatusCode == 401) {
        return (
            <Alert severity="error" sx={{ marginBottom: '16px' }}>
                <Localized
                    de="Der eingegebene Code ist ungültig."
                    fr="Le code saisi n'est pas valide."
                    it="Il codice inserito non è valido."
                    en="The entered code is invalid."
                />
            </Alert>
        );
    }

    if (confirmOtpState.httpStatusCode == 403) {
        return (
            <Alert severity="error" sx={{ marginBottom: '16px' }}>
                <Localized
                    de="Sie besitzen nicht die Berechtigung, um bei diesem Login das Passwort zu ändern."
                    fr="Vous n'avez pas l'autorisation de modifier le mot de passe pour cette connexion."
                    it="Non hai l'autorizzazione per cambiare la password per questo accesso."
                    en="You do not have permission to change the password for this login."
                />
            </Alert>
        );
    }

    return (
        <Alert severity="error" sx={{ marginBottom: '16px' }}>
            <Localized
                de="Ein unerwarteter Fehler ist aufgetreten."
                fr="Une erreur inattendue s'est produite."
                it="Si è verificato un errore imprevisto."
                en="An unexpected error has occurred."
            />
        </Alert>
    );
}

type OtpOutletContext = {
    refetchAfterSuccess?: () => void;
};

function useOtpOutletContext() {
    return useOutletContext<OtpOutletContext>();
}

export function OtpOutlet({ context }: { context: OtpOutletContext }) {
    return <Outlet context={context} />;
}
